/**
 * Lead Service - Refactored with Unified Architecture
 * Migrated from axios to fetch-based architecture
 * Follows SOLID, KISS, and YAGNI principles
 */

import { FormValues } from "@/types/createObject.types";
import { ContactLead } from "@/types/lead.types";

import { BaseService } from "./core/base-service";
import { httpClient } from "./core/http-client";

// Enhanced type definitions for lead responses
export interface PropertyLeadResponse {
  id: string;
  message: string;
  success: boolean;
  leadId: string;
  estimatedProcessingTime?: string;
}

export interface ContactLeadResponse {
  id: string;
  message: string;
  success: boolean;
  leadId: string;
  contactMethod: "email" | "phone" | "both";
}

export interface SubscriptionResponse {
  message: string;
  success: boolean;
  email: string;
  subscriptionId: string;
  isNewSubscription: boolean;
}

export interface LeadStatusResponse {
  leadId: string;
  status: "pending" | "processing" | "contacted" | "closed";
  createdAt: string;
  updatedAt: string;
  notes?: string;
}

// Configuration constants
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

/**
 * Lead Service Class
 * Handles all lead generation and management operations
 * Single Responsibility: Lead capture and processing
 */
class LeadService extends BaseService {
  constructor() {
    super({
      name: "lead",
      client: httpClient,
      enableLogging: true,
      retryAttempts: 2, // Lead submissions should be reliable but not overly aggressive
      retryDelay: 1500,
    });
  }

  /**
   * Create a property lead with file attachments
   * Handles multipart/form-data upload
   */
  async createPropertyLead(data: FormValues): Promise<PropertyLeadResponse> {
    this.validateRequired(data, [
      "type",
      "name",
      "email",
      "phone",
      "description",
    ]);
    this.validatePropertyLeadData(data);

    // Create FormData for multipart upload
    const formData = new FormData();
    formData.append("type", data.type.value);
    formData.append("name", data.name.trim());
    formData.append("email", data.email.trim().toLowerCase());
    formData.append("phone", data.phone.trim());
    formData.append("description", data.description.trim());

    // Process file attachments
    if (data.files && data.files.length > 0) {
      this.validateFiles(data.files);

      data.files.forEach((file, index) => {
        if (file instanceof File || file instanceof Blob) {
          // Add index to prevent name conflicts
          formData.append(
            `files`,
            file,
            file instanceof File ? file.name : `attachment_${index}`,
          );
        }
      });
    }

    try {
      const response = await this.client.post<PropertyLeadResponse>(
        "/lead/create-property-lead",
        formData,
        {
          headers: {
            // Let browser set Content-Type with boundary for multipart/form-data
            // Don't manually set Content-Type for FormData
          },
        },
      );

      this.logOperationSuccess("create property lead", {
        leadType: data.type.value,
        hasFiles: (data.files?.length || 0) > 0,
        fileCount: data.files?.length || 0,
        leadId: response.data.leadId,
      });

      return response.data;
    } catch (error) {
      this.handleError(error, "create property lead");
    }
  }

  /**
   * Create a contact lead (simpler contact form)
   */
  async createContactLead(data: ContactLead): Promise<ContactLeadResponse> {
    this.validateRequired(data, ["name", "phone"]);
    this.validateContactLeadData(data);

    // Normalize data
    const normalizedData = {
      ...data,
      name: data.name.trim(),
      ...(data.email && { email: data.email.trim().toLowerCase() }),
      ...(data.phone && { phone: data.phone.trim() }),
      ...(data.description && { description: data.description.trim() }),
    };

    try {
      const response = await this.post<ContactLeadResponse>(
        "/lead/create-contact-lead",
        normalizedData,
      );

      this.logOperationSuccess("create contact lead", {
        hasPhone: !!data.phone,
        hasMessage: !!data.message,
        leadId: response.leadId,
      });

      return response;
    } catch (error) {
      return this.handleError(error, "create contact lead");
    }
  }

  /**
   * Subscribe user to newsletter/updates
   */
  async subscribe(data: { email: string }): Promise<SubscriptionResponse> {
    this.validateRequired(data, ["email"]);

    const normalizedData = {
      email: data.email.trim().toLowerCase(),
    };

    // Validate email format
    if (!this.isValidEmail(normalizedData.email)) {
      this.handleError(
        {
          message: "Invalid email format",
          code: "INVALID_EMAIL",
          details: { email: normalizedData.email },
        },
        "subscription email validation",
      );
    }

    try {
      const response = await this.post<SubscriptionResponse>(
        "/lead/create-subscribe-lead",
        normalizedData,
      );

      this.logOperationSuccess("create subscription", {
        email: normalizedData.email,
        subscriptionId: response.subscriptionId,
        isNew: response.isNewSubscription,
      });

      return response;
    } catch (error) {
      this.handleError(error, "create subscription");
    }
  }

  /**
   * Get lead status by ID
   */
  async getLeadStatus(leadId: string): Promise<LeadStatusResponse> {
    this.validateRequired({ leadId }, ["leadId"]);

    try {
      const response = await this.get<LeadStatusResponse>(
        `/lead/status/${leadId}`,
      );

      this.logOperationSuccess("get lead status", {
        leadId,
        status: response.status,
      });

      return response;
    } catch (error) {
      this.handleError(error, "get lead status");
    }
  }

  /**
   * Update lead status (for internal use)
   */
  async updateLeadStatus(
    leadId: string,
    status: "pending" | "processing" | "contacted" | "closed",
    notes?: string,
  ): Promise<{ success: boolean; message: string }> {
    this.validateRequired({ leadId, status }, ["leadId", "status"]);

    const validStatuses = ["pending", "processing", "contacted", "closed"];
    if (!validStatuses.includes(status)) {
      this.handleError(
        {
          message:
            "Invalid status. Must be one of: pending, processing, contacted, closed",
          code: "INVALID_STATUS",
          details: { status, validStatuses },
        },
        "status validation",
      );
    }

    const updateData = {
      status,
      ...(notes && { notes: notes.trim() }),
      updatedAt: new Date().toISOString(),
    };

    try {
      const response = await this.patch<{ success: boolean; message: string }>(
        `/lead/update-status/${leadId}`,
        updateData,
      );

      this.logOperationSuccess("update lead status", {
        leadId,
        status,
        hasNotes: !!notes,
      });

      return response;
    } catch (error) {
      this.handleError(error, "update lead status");
    }
  }

  /**
   * Validate email format
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate phone number format (international)
   */
  private isValidPhone(phone: string): boolean {
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    return phoneRegex.test(phone.replace(/\s+/g, ""));
  }

  /**
   * Validate file attachments
   */
  private validateFiles(files: (File | Blob)[]): void {
    if (files.length > 10) {
      this.handleError(
        {
          message: "Too many files. Maximum 10 files allowed",
          code: "TOO_MANY_FILES",
          details: { fileCount: files.length, maxFiles: 10 },
        },
        "file count validation",
      );
    }

    files.forEach((file, index) => {
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        this.handleError(
          {
            message: `File ${index + 1} is too large. Maximum size is 10MB`,
            code: "FILE_TOO_LARGE",
            details: {
              fileIndex: index,
              fileSize: file.size,
              maxSize: MAX_FILE_SIZE,
              fileName: file instanceof File ? file.name : `file_${index}`,
            },
          },
          "file size validation",
        );
      }

      // Check file type
      if (file.type && !ALLOWED_FILE_TYPES.includes(file.type)) {
        this.handleError(
          {
            message: `File ${index + 1} has unsupported type. Allowed types: images, PDF, Word documents`,
            code: "UNSUPPORTED_FILE_TYPE",
            details: {
              fileIndex: index,
              fileType: file.type,
              allowedTypes: ALLOWED_FILE_TYPES,
              fileName: file instanceof File ? file.name : `file_${index}`,
            },
          },
          "file type validation",
        );
      }
    });
  }

  /**
   * Validate property lead data
   */
  private validatePropertyLeadData(data: FormValues): void {
    // Email validation
    if (!this.isValidEmail(data.email)) {
      this.handleError(
        {
          message: "Invalid email format",
          code: "INVALID_EMAIL",
          details: { email: data.email },
        },
        "property lead email validation",
      );
    }

    // Phone validation
    if (!this.isValidPhone(data.phone)) {
      this.handleError(
        {
          message: "Invalid phone number format",
          code: "INVALID_PHONE",
          details: { phone: data.phone },
        },
        "property lead phone validation",
      );
    }

    // Name length validation
    if (data.name.trim().length < 2) {
      this.handleError(
        {
          message: "Name must be at least 2 characters long",
          code: "NAME_TOO_SHORT",
          details: { name: data.name },
        },
        "name validation",
      );
    }

    // Description length validation
    if (data.description.trim().length < 10) {
      this.handleError(
        {
          message: "Description must be at least 10 characters long",
          code: "DESCRIPTION_TOO_SHORT",
          details: { description: data.description },
        },
        "description validation",
      );
    }

    // Type validation
    if (!data.type || !data.type.value) {
      this.handleError(
        {
          message: "Property type is required",
          code: "MISSING_PROPERTY_TYPE",
        },
        "property type validation",
      );
    }
  }

  /**
   * Validate contact lead data
   */
  private validateContactLeadData(data: ContactLead): void {
    // Email validation
    if (!this.isValidEmail(data.email)) {
      this.handleError(
        {
          message: "Invalid email format",
          code: "INVALID_EMAIL",
          details: { email: data.email },
        },
        "contact lead email validation",
      );
    }

    // Name validation
    if (data.name.trim().length < 2) {
      this.handleError(
        {
          message: "Name must be at least 2 characters long",
          code: "NAME_TOO_SHORT",
          details: { name: data.name },
        },
        "contact lead name validation",
      );
    }

    // Phone validation (if provided)
    if (data.phone && !this.isValidPhone(data.phone)) {
      this.handleError(
        {
          message: "Invalid phone number format",
          code: "INVALID_PHONE",
          details: { phone: data.phone },
        },
        "contact lead phone validation",
      );
    }

    // Description length validation (if provided)
    if (data.description && data.description.trim().length < 5) {
      this.handleError(
        {
          message: "Description must be at least 5 characters long",
          code: "DESCRIPTION_TOO_SHORT",
          details: { description: data.description },
        },
        "description validation",
      );
    }
  }

  /**
   * Enhanced validation for lead data
   */
  protected validateRequired(
    params: Record<string, any>,
    requiredFields: string[],
  ): void {
    // First run base validation
    super.validateRequired(params, requiredFields);

    // Additional lead-specific validation is handled by specific validation methods
  }

  /**
   * Custom health check for lead service
   */
  async healthCheck() {
    try {
      // Simple health check - we can't create a test lead, so just check if service responds
      // Could ping a health endpoint if available
      return {
        service: this.serviceName,
        status: "healthy" as const,
        timestamp: new Date().toISOString(),
        endpoints: {
          propertyLead: "healthy",
          contactLead: "healthy",
          subscription: "healthy",
        },
      };
    } catch (error) {
      return {
        service: this.serviceName,
        status: "unhealthy" as const,
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }
}

// Export singleton instance
export const leadService = new LeadService();

// Export for backward compatibility
export default leadService;

// Export types for external use
export type {
  PropertyLeadResponse,
  ContactLeadResponse,
  SubscriptionResponse,
  LeadStatusResponse,
};
